import { Close } from '@mui/icons-material';
import { Box, Button, Stack, SxProps, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CAppPaper from '../../../../app/components/CAppPaper';
import AppCustomIcon from '../../../../app/features/icon/AppIcon';
import useUserCompanys from '../../hooks/useUserCompanys';
import useUserProfile from '../../hooks/useUserProfile';
import useUserUpdateProfile from '../../hooks/useUserUpdateProfile';
import IUserProfile from '../../interfaces/IUserProfile';
import langUserProfile from '../../langs/langUserProfile';
import { FieldBox } from '../../pages/PUserProfile';

type VUserProfileInformationProps = {
	sxStyle: SxProps;
	sxTextField: SxProps;
	sxEditButton: SxProps;
	disableEdit: boolean;
};

const VUserProfileInformation = (props: VUserProfileInformationProps) => {
	const { sxStyle, sxTextField, sxEditButton, disableEdit } = props;

	const { t } = useTranslation();
	const { investorProfile } = useUserProfile();
	const { companies } = useUserCompanys();

	const [profileData, setProfileData] = useState<IUserProfile>();

	useEffect(() => {
		if (investorProfile) {
			setProfileData({
				...investorProfile,
			});
		}
	}, [investorProfile]);

	const [editBlock, setEditBlock] = useState(false);

	const handleClickEditInformation = () => {
		setEditBlock(!editBlock);
	};

	const { mutate: updateProfile } = useUserUpdateProfile();

	const handleClickSaveInformation = () => {
		if (profileData) {
			updateProfile({
				firstName: profileData.firstName,
				lastName: profileData.lastName,
			});
			setEditBlock(!editBlock);
		}
	};

	return (
		<>
			{profileData ? (
				<CAppPaper sx={sxStyle} variant="outlined">
					<Stack direction="row" alignItems="center" justifyContent="space-between">
						<Box className="title">
							<AppCustomIcon tag="user_account" className="icon" />{' '}
							{t(langUserProfile.title_personal_information.tag)}
						</Box>

						<Box>
							{editBlock ? (
								<>
									<Button
										variant="text"
										color="secondary"
										size="small"
										sx={{ ...sxEditButton, marginRight: '20px' }}
										type="button"
										startIcon={<Close />}
										onClick={() => handleClickEditInformation()}
									>
										{t(langUserProfile.btn_cancel.tag)}
									</Button>
									<Button
										variant="contained"
										color="secondary"
										size="small"
										sx={sxEditButton}
										disabled={disableEdit}
										startIcon={<AppCustomIcon tag="save" className="icon" />}
										onClick={() => handleClickSaveInformation()}
									>
										{t(langUserProfile.btn_save.tag)}
									</Button>
								</>
							) : (
								<Button
									variant="text"
									color="secondary"
									size="small"
									sx={sxEditButton}
									disabled={disableEdit}
									startIcon={<AppCustomIcon tag="edit" className="icon" />}
									onClick={() => handleClickEditInformation()}
								>
									{t(langUserProfile.btn_edit.tag)}
								</Button>
							)}
						</Box>
					</Stack>
					{editBlock ? (
						<>
							<Box>
								<FieldBox
									label={t(langUserProfile.field_firstname.tag)}
									value={profileData.firstName}
									bEdit={editBlock}
									component={
										<TextField
											fullWidth
											value={profileData.firstName}
											onChange={(e) =>
												setProfileData((prevData) => ({
													...(prevData as IUserProfile),
													firstName: e.target.value,
												}))
											}
											variant="outlined"
											sx={sxTextField}
										/>
									}
								/>
								<FieldBox
									label={t(langUserProfile.field_lastname.tag)}
									value={profileData.lastName}
									bEdit={editBlock}
									component={
										<TextField
											fullWidth
											value={profileData.lastName}
											onChange={(e) =>
												setProfileData((prevData) => ({
													...(prevData as IUserProfile),
													lastName: e.target.value,
												}))
											}
											variant="outlined"
											sx={sxTextField}
										/>
									}
								/>
								<FieldBox
									label={t(langUserProfile.field_email.tag)}
									value={profileData.email}
									bEdit={editBlock}
									readonly
									component={
										<TextField
											fullWidth
											value={profileData.email}
											onChange={(e) =>
												setProfileData((prevData) => ({
													...(prevData as IUserProfile),
													email: e.target.value,
												}))
											}
											variant="outlined"
											sx={sxTextField}
										/>
									}
								/>
								<FieldBox
									label={
										companies?.length === 1
											? t(langUserProfile.field_my_organisation.tag)
											: (companies?.length ?? 0) > 1
											? t(langUserProfile.field_my_organisations.tag)
											: ''
									}
									value={companies ? companies.map((x) => x.name).join(', ') : null}
									readonly
								/>
							</Box>
						</>
					) : (
						<Box sx={{ marginTop: '20px' }}>{t(langUserProfile.label_for_block_information.tag)}</Box>
					)}
				</CAppPaper>
			) : (
				<></>
			)}
		</>
	);
};

export default VUserProfileInformation;
